import { CloseOutlined, PlusOutlined } from '@ant-design/icons';
import { Card } from 'antd';
import React, { useState } from 'react';
import { useFilterGroupsActions } from '../hooks';
import type { FilterGroup, GroupOperator } from '../types/filter-panel.types';
import { FilterConditionRow } from './FilterConditionRow';
import { Icon } from '@ui/assets/icons/IconWrapper';
import Styles from './Styles.module.scss';
import { Button } from '@ui/components/button';

interface FilterGroupCardProps {
    group: FilterGroup;
    groupIndex: number;
}

export const FilterGroupCard: React.FC<FilterGroupCardProps> = ({ group, groupIndex }) => {
    const [showOperatorSelection, setShowOperatorSelection] = useState(false);

    // Use constate hooks - minimal imports since child components get their own context
    const { addCondition, removeFilterGroup } = useFilterGroupsActions();

    const handleAddCondition = (operator: GroupOperator) => {
        setShowOperatorSelection(false);
        addCondition(group.id, operator);
    };

    // If there are no conditions, don't show operator selection
    const hasConditions = group.conditions.length > 0;

    return (
        <Card
            key={group.id}
            data-group-id={group.id}
            type="inner"
            size="small"
            className={Styles.filterGroupCard}
            title={
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1">
                        <span className="text-[12px] font-semibold leading-[20px] text-[#434343]">
                            Filter Group {groupIndex + 1}
                        </span>
                        <div
                            className="cursor-pointer"
                            onClick={() => removeFilterGroup(group.id)}
                        >
                            <Icon
                                name="Delete"
                                size={20}
                            />
                        </div>
                    </div>
                    {showOperatorSelection ? (
                        <div className="flex h-[34px] items-center justify-center gap-[10px]">
                            <Button
                                type="default"
                                onClick={() => handleAddCondition('AND')}
                                className={Styles.logicalOperatorButton}
                            >
                                AND
                            </Button>
                            <Button
                                type="default"
                                onClick={() => handleAddCondition('OR')}
                                className={Styles.logicalOperatorButton}
                            >
                                OR
                            </Button>
                            <Button
                                type="text"
                                onClick={() => setShowOperatorSelection(false)}
                                icon={<CloseOutlined color="#444" />}
                                className="!h-[20px] !w-[20px] !rounded-full hover:!bg-transparent focus:!bg-transparent"
                            />
                        </div>
                    ) : (
                        <Button
                            type="default"
                            icon={<PlusOutlined />}
                            onClick={() => {
                                // If this will be the first condition, add directly without asking for operator
                                // If there are existing conditions, show operator selection
                                if (hasConditions) {
                                    setShowOperatorSelection(true);
                                } else {
                                    // First condition doesn't need an operator
                                    addCondition(group.id);
                                }
                            }}
                        >
                            Add Condition
                        </Button>
                    )}
                </div>
            }
            style={{ marginBottom: 16 }}
        >
            {group.conditions.map((condition, conditionIndex) => (
                <FilterConditionRow
                    key={condition.id}
                    groupId={group.id}
                    condition={condition}
                    conditionIndex={conditionIndex}
                    groupOperator={group.operator}
                />
            ))}
        </Card>
    );
};
